import Box from "@material-ui/core/Box";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";

import { capitalizeFirstLetter } from "app/main/utils/tableUtils";
import { DisplaySection } from "app/main/utils/uiUtils";

import { ordinals, getValidatedPathsStats } from "app/main/strategy/utils/strategyUtils";
import TruckInfo from "./TruckInfo";
import TableCarousel from "./TableCarousel";
import MovesTableView from "./MovesTableView";

const formatCurrency = (value) => (value ? `$${value.toFixed(2)}` : "N/A");

const useStyles = makeStyles((theme) => ({
	statsContainer: {
		marginTop: theme.spacing(3),
		padding: theme.spacing(2),
		backgroundColor: theme.palette.background.paper,
		borderRadius: theme.shape.borderRadius,
	},
	primaryStats: {
		display: "flex",
		justifyContent: "space-around",
		marginBottom: theme.spacing(3),
	},
	primaryStat: {
		textAlign: "center",
	},
	primaryValue: {
		fontSize: "2rem",
		fontWeight: "bold",
		color: theme.palette.primary.main,
	},
	primaryLabel: {
		fontSize: "1rem",
		color: theme.palette.text.secondary,
	},
	primarySubValue: {
		fontSize: "1rem",
		color: theme.palette.text.secondary,
		marginTop: theme.spacing(0.5),
	},
	secondaryStats: {
		display: "grid",
		gridTemplateColumns: "repeat(3, 1fr)",
		gap: theme.spacing(2),
	},
	secondaryStat: {
		textAlign: "center",
	},
	secondaryValue: {
		fontSize: "1.1rem",
		fontWeight: "medium",
	},
	secondaryLabel: {
		fontSize: "0.875rem",
		color: theme.palette.text.secondary,
	},
	secondarySubValue: {
		fontSize: "0.875rem",
		color: theme.palette.text.secondary,
		marginTop: theme.spacing(0.5),
	},
}));

const StatsDisplay = ({ selectedPaths, rawPaths }) => {
	const classes = useStyles();
	const stats = getValidatedPathsStats(selectedPaths, rawPaths);

	return (
		<DisplaySection title="Path Statistics">
			<div className={classes.statsContainer}>
				<div className={classes.primaryStats}>
					<div className={classes.primaryStat}>
						<Typography className={classes.primaryValue}>{formatCurrency(stats.min_total_payment)}</Typography>
						<Typography className={classes.primarySubValue}>Avg: {formatCurrency(stats.avg_total_payment)}</Typography>
						<Typography className={classes.primaryLabel}>Min Total Payment</Typography>
					</div>
					<div className={classes.primaryStat}>
						<Typography className={classes.primaryValue}>{stats.min_total_rpm.toFixed(2)}</Typography>
						<Typography className={classes.primarySubValue}>Avg: {stats.avg_total_rpm.toFixed(2)}</Typography>
						<Typography className={classes.primaryLabel}>Min Total RPM</Typography>
					</div>
					<div className={classes.primaryStat}>
						<Typography className={classes.primaryValue}>{stats.max_total_miles}</Typography>
						<Typography className={classes.primarySubValue}>Avg: {Math.round(stats.avg_total_miles)}</Typography>
						<Typography className={classes.primaryLabel}>Max Total Miles</Typography>
					</div>
				</div>
				<div className={classes.secondaryStats}>
					<div className={classes.secondaryStat}>
						<Typography className={classes.secondaryValue}>{stats.num_paths}</Typography>
						<Typography className={classes.secondaryLabel}>Total Paths</Typography>
					</div>
					<div className={classes.secondaryStat}>
						<Typography className={classes.secondaryValue}>{stats.num_flex}</Typography>
						<Typography className={classes.secondaryLabel}>Flex Paths</Typography>
					</div>
					<div className={classes.secondaryStat}>
						<Typography className={classes.secondaryValue}>{stats.min_total_volume.toFixed(1)}</Typography>
						<Typography className={classes.secondarySubValue}>Avg: {stats.avg_total_volume.toFixed(1)}</Typography>
						<Typography className={classes.secondaryLabel}>Min Total Volume</Typography>
					</div>
					<div className={classes.secondaryStat}>
						<Typography className={classes.secondaryValue}>{stats.max_duration}</Typography>
						<Typography className={classes.secondarySubValue}>Avg: {stats.avg_duration.toFixed(1)}</Typography>
						<Typography className={classes.secondaryLabel}>Max Duration (days)</Typography>
					</div>
					<div className={classes.secondaryStat}>
						<Typography className={classes.secondaryValue}>{stats.visited_states.join(", ")}</Typography>
						<Typography className={classes.secondaryLabel}>Visited States</Typography>
					</div>
				</div>
			</div>
		</DisplaySection>
	);
};

const StrategySummary = ({
	strategy,
	strategyReqData,
	truckInfo,
	saving,
	setTablePaths,
	tablePaths,
	selectedPaths,
	summary,
	selectAll,
	setSelectAll,
}) => {
	const rawPaths = strategy?.response?.paths;
	return (
		<>
			{strategy && (
				<Box p={2}>
					{truckInfo && <TruckInfo truckInfo={truckInfo} strategy={strategy} />}
					<StatsDisplay selectedPaths={selectedPaths} rawPaths={rawPaths} />
					<TableCarousel
						items={summary.summaryArr.map((summary, i) => (
							<DisplaySection
								key={`${ordinals[i]}-ordinal-section`}
								title={`${capitalizeFirstLetter(ordinals[i])} Loaded Moves`}
							>
								<MovesTableView
									key={ordinals[i]}
									tableIndex={i}
									summary={summary}
									tablePaths={tablePaths}
									setTablePaths={setTablePaths}
									selectAll={selectAll}
									setSelectAll={setSelectAll}
									truckInfo={truckInfo}
									strategyReqData={strategyReqData}
									selectedPaths={selectedPaths}
									saving={saving}
								/>
							</DisplaySection>
						))}
					/>
				</Box>
			)}
		</>
	);
};

export default StrategySummary;
